import { Component, Inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CatalogProduct } from '@core/interfaces/catalog.interface';
import { CatalogsService } from '@core/services/catalogs/catalogs.service';
import { finalize } from 'rxjs';
import Swal from 'sweetalert2';

export interface ProductDialogData {
  draftId: string;
  /** índice del producto a editar; null = producto nuevo */
  index: number | null;
  product?: CatalogProduct;
}

@Component({
  selector: 'app-product-form-dialog',
  templateUrl: './product-form-dialog.component.html',
  styleUrls: ['./product-form-dialog.component.scss'],
})
export class ProductFormDialogComponent {
  public saving = false;
  public uploading = false;
  public images: string[] = [];

  public form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    description: [''],
    price: [''],
  });

  constructor(
    private fb: FormBuilder,
    private catalogsService: CatalogsService,
    private dialogRef: MatDialogRef<ProductFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProductDialogData,
  ) {
    if (data.product) {
      this.form.patchValue({
        name: data.product.name || '',
        description: data.product.description || '',
        price: data.product.price || '',
      });
      this.images = [...(data.product.images || [])];
    }
  }

  get isEdit(): boolean {
    return this.data.index !== null;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const files = Array.from(input.files);
    input.value = '';

    this.uploading = true;
    let pending = files.length;

    files.forEach((file) => {
      this.catalogsService.uploadImage(file, 'catalogs').subscribe({
        next: (url) => {
          this.images.push(url);
          if (--pending === 0) this.uploading = false;
        },
        error: () => {
          if (--pending === 0) this.uploading = false;
          Swal.fire('Error', `No se pudo subir "${file.name}".`, 'error');
        },
      });
    });
  }

  removeImage(index: number): void {
    this.images.splice(index, 1);
  }

  save(): void {
    if (this.form.invalid || this.uploading) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const payload = {
      name: (raw.name || '').trim(),
      description: (raw.description || '').trim(),
      price: (raw.price || '').trim(),
      images: this.images,
    };

    this.saving = true;

    if (this.isEdit) {
      this.catalogsService
        .updateProduct(this.data.draftId, this.data.index as number, payload)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: (resp) => this.dialogRef.close(resp.draft),
          error: () =>
            Swal.fire('Error', 'No se pudo actualizar el producto.', 'error'),
        });
      return;
    }

    // Nuevo: crear y luego, si hay imágenes, asignarlas por índice.
    this.catalogsService
      .addProduct(this.data.draftId, {
        name: payload.name,
        description: payload.description,
        price: payload.price,
      })
      .subscribe({
        next: (resp) => {
          const newIndex = resp.draft.products.length - 1;
          if (this.images.length === 0) {
            this.saving = false;
            this.dialogRef.close(resp.draft);
            return;
          }
          this.catalogsService
            .updateProduct(this.data.draftId, newIndex, payload)
            .pipe(finalize(() => (this.saving = false)))
            .subscribe({
              next: (r2) => this.dialogRef.close(r2.draft),
              error: () => {
                this.dialogRef.close(resp.draft);
                Swal.fire(
                  'Aviso',
                  'El producto se creó pero no se pudieron guardar las imágenes.',
                  'warning',
                );
              },
            });
        },
        error: () => {
          this.saving = false;
          Swal.fire('Error', 'No se pudo crear el producto.', 'error');
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}
